import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Plus, Trash2, Copy, RefreshCw, Eye, EyeOff, ShieldCheck, Package, Pencil, Check, X, Settings2 } from "lucide-react";

const DEFAULT_DEPARTMENTS = ["Sales", "Service", "Support", "Billing", "Management"];

function generatePassword() {
  const upper = "ABCDEFGHJKLMNPQRSTUVWXYZ";
  const lower = "abcdefghijkmnpqrstuvwxyz";
  const digits = "23456789";
  const symbols = "@#$!";
  const pool = upper + lower + digits + symbols;
  const pick = (s) => s[Math.floor(Math.random() * s.length)];
  let p = pick(upper) + pick(lower) + pick(digits) + pick(symbols);
  for (let i = 0; i < 6; i++) p += pick(pool);
  return p.split("").sort(() => Math.random() - 0.5).join("");
}

export default function Employees() {
  const [emps, setEmps] = useState([]);
  const [open, setOpen] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    department: "Sales",
    password: "Employee@12345",
  });
  const [lastCreated, setLastCreated] = useState(null);
  const [depts, setDepts] = useState(DEFAULT_DEPARTMENTS);
  const [deptDialogOpen, setDeptDialogOpen] = useState(false);
  const [newDeptName, setNewDeptName] = useState("");
  const [editingDept, setEditingDept] = useState(null); // { original, value }
  const [savingDept, setSavingDept] = useState(false);

  const load = async () => {
    const { data } = await api.get("/employees");
    setEmps(data);
  };

  const loadDepartments = async () => {
    try {
      const { data } = await api.get("/departments");
      setDepts(data?.length ? data : DEFAULT_DEPARTMENTS);
    } catch {
      setDepts(DEFAULT_DEPARTMENTS);
    }
  };

  useEffect(() => {
    load();
    loadDepartments();
  }, []);

  useEffect(() => {
    if (depts.length && !depts.includes(form.department)) {
      setForm((f) => ({ ...f, department: depts[0] }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [depts]);

  const addDepartment = async () => {
    const name = newDeptName.trim();
    if (!name) return;
    setSavingDept(true);
    try {
      const { data } = await api.post("/departments", { name });
      setDepts(data);
      setNewDeptName("");
      toast.success(`Added ${name}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add department");
    } finally {
      setSavingDept(false);
    }
  };

  const saveRenameDept = async () => {
    if (!editingDept) return;
    const newName = editingDept.value.trim();
    if (!newName) return toast.error("Department name is required");
    setSavingDept(true);
    try {
      const { data } = await api.patch(
        `/departments/${encodeURIComponent(editingDept.original)}`,
        { name: newName },
      );
      setDepts(data);
      setEditingDept(null);
      toast.success("Department renamed");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not rename department");
    } finally {
      setSavingDept(false);
    }
  };

  const deleteDepartment = async (name) => {
    try {
      const { data } = await api.delete(`/departments/${encodeURIComponent(name)}`);
      setDepts(data);
      toast.success("Department removed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not remove department");
    }
  };

  const create = async () => {
    try {
      const { data } = await api.post("/employees", form);
      toast.success(`Added ${data.name}`);
      setLastCreated({ email: data.email, password: data.default_password });
      setForm({ name: "", email: "", phone: "", department: depts[0] || "Sales", password: "Employee@12345" });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add employee");
    }
  };

  // Editing an existing employee: { id, name, email, phone, department, password }
  const [editing, setEditing] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [showEditPw, setShowEditPw] = useState(false);
  const [lastReset, setLastReset] = useState(null);

  const startEdit = (emp) => {
    setEditing({
      id: emp.id,
      name: emp.name || "",
      email: emp.email || "",
      phone: emp.phone || "",
      department: emp.department || depts[0] || "Sales",
      password: "",
    });
    setShowEditPw(false);
    setLastReset(null);
  };

  const saveEdit = async () => {
    if (!editing) return;
    const { id, password, ...fields } = editing;
    const body = { ...fields };
    if (password.trim()) body.password = password.trim();
    setSavingEdit(true);
    try {
      const { data } = await api.patch(`/employees/${id}`, body);
      toast.success(`Saved ${data.name}`);
      await load();
      if (body.password) {
        // Keep the dialog open so the owner can copy the new login.
        setLastReset({ email: data.email, password: body.password });
        setEditing({ ...editing, password: "" });
      } else {
        setEditing(null);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save changes");
    } finally {
      setSavingEdit(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/employees/${id}`);
      toast.success("Employee removed");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to remove employee");
    }
  };

  const togglePermission = async (emp, key, value) => {
    const labels = {
      can_create_tasks: value ? `${emp.name} can now create tasks` : `Task permission revoked from ${emp.name}`,
      can_manage_marketplace: value ? `${emp.name} can now manage the catalog` : `Catalog access revoked from ${emp.name}`,
    };
    try {
      await api.patch(`/employees/${emp.id}/permissions`, { [key]: value });
      toast.success(labels[key] || "Updated");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update permission");
    }
  };

  return (
    <div className="p-6 md:p-10 max-w-[1400px]" data-testid="owner-employees">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="mono text-xs uppercase tracking-widest text-[#FF5000]">
            Team
          </div>
          <h1 className="font-heading text-3xl font-bold mt-2">Employees</h1>
          <p className="text-slate-500 mt-1 text-sm">
            Add or remove people who can use the mobile app to call your customers.
          </p>
        </div>
        <div className="flex items-center gap-2">
        <Dialog
          open={deptDialogOpen}
          onOpenChange={(v) => {
            setDeptDialogOpen(v);
            if (!v) setEditingDept(null);
          }}
        >
          <DialogTrigger asChild>
            <Button variant="outline" data-testid="departments-manage">
              <Settings2 className="w-4 h-4 mr-1" /> Manage departments
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Departments</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div className="border border-slate-200 rounded-md divide-y">
                {depts.map((d) => (
                  <div key={d} className="flex items-center gap-2 px-3 py-2" data-testid={`department-row-${d}`}>
                    {editingDept?.original === d ? (
                      <>
                        <Input
                          value={editingDept.value}
                          onChange={(e) => setEditingDept({ original: d, value: e.target.value })}
                          className="h-8 flex-1"
                          autoFocus
                          data-testid={`department-edit-input-${d}`}
                        />
                        <button
                          type="button"
                          onClick={saveRenameDept}
                          disabled={savingDept}
                          className="text-emerald-600 hover:text-emerald-700"
                          data-testid={`department-save-${d}`}
                        >
                          <Check className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => setEditingDept(null)}
                          className="text-slate-400 hover:text-slate-700"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </>
                    ) : (
                      <>
                        <span className="flex-1 text-sm">{d}</span>
                        <button
                          type="button"
                          onClick={() => setEditingDept({ original: d, value: d })}
                          className="text-slate-400 hover:text-slate-700"
                          data-testid={`department-edit-${d}`}
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => deleteDepartment(d)}
                          className="text-slate-400 hover:text-red-500"
                          data-testid={`department-delete-${d}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </>
                    )}
                  </div>
                ))}
                {depts.length === 0 && (
                  <p className="text-sm text-slate-500 text-center py-4">No departments yet.</p>
                )}
              </div>
              <div className="flex gap-2">
                <Input
                  placeholder="New department name"
                  value={newDeptName}
                  onChange={(e) => setNewDeptName(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && addDepartment()}
                  data-testid="department-new-input"
                />
                <Button onClick={addDepartment} disabled={savingDept || !newDeptName.trim()} data-testid="department-add">
                  <Plus className="w-4 h-4 mr-1" /> Add
                </Button>
              </div>
              <p className="mono text-[10px] text-slate-500">
                Renaming a department updates it everywhere — employees in that team and any IVR menu
                routed to it move automatically.
              </p>
            </div>
          </DialogContent>
        </Dialog>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button
              className="bg-[#0055FF] hover:bg-[#0040CC]"
              data-testid="employee-new"
            >
              <Plus className="w-4 h-4 mr-1" /> Add employee
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add employee</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div className="space-y-2">
                <Label>Name</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="employee-name"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    data-testid="employee-email"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Phone</Label>
                  <Input
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    data-testid="employee-phone"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Department</Label>
                <Select
                  value={form.department}
                  onValueChange={(v) => setForm({ ...form, department: v })}
                >
                  <SelectTrigger data-testid="employee-department">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {depts.map((d) => (
                      <SelectItem key={d} value={d}>
                        {d}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="flex items-center justify-between">
                  <span>Password</span>
                  <button
                    type="button"
                    className="mono text-[10px] uppercase tracking-widest text-[#0055FF] flex items-center gap-1"
                    onClick={() => setForm({ ...form, password: generatePassword() })}
                    data-testid="employee-generate-password"
                  >
                    <RefreshCw className="w-3 h-3" /> Generate strong
                  </button>
                </Label>
                <div className="relative">
                  <Input
                    type={showPw ? "text" : "password"}
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    minLength={6}
                    className="pr-9"
                    data-testid="employee-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw((v) => !v)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                    aria-label="toggle password visibility"
                  >
                    {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <p className="mono text-[10px] text-slate-500">
                  Share this password with your employee — they&apos;ll use it to log in
                  to the mobile app at <span className="text-slate-800">/app</span>.
                </p>
              </div>
              {lastCreated && (
                <div className="border border-emerald-200 bg-emerald-50 rounded-md p-3 text-sm">
                  <div className="font-medium text-emerald-800">
                    Share these credentials with your employee:
                  </div>
                  <div className="mono text-xs text-emerald-900 mt-1">
                    {lastCreated.email} / {lastCreated.password}
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    className="mt-2"
                    onClick={() =>
                      navigator.clipboard.writeText(
                        `${lastCreated.email} / ${lastCreated.password}`
                      )
                    }
                  >
                    <Copy className="w-3 h-3 mr-1" /> Copy
                  </Button>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button
                onClick={create}
                className="bg-[#0055FF] hover:bg-[#0040CC]"
                data-testid="employee-submit"
              >
                Create employee
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <Card className="border-slate-200 shadow-none">
        <CardHeader className="border-b">
          <CardTitle className="font-heading text-lg">
            {emps.length} people on your team
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm min-w-[860px]">
            <thead className="bg-slate-50 mono text-[10px] uppercase tracking-widest text-slate-500">
              <tr>
                <th className="text-left px-5 py-3">Name</th>
                <th className="text-left px-5 py-3">Email</th>
                <th className="text-left px-5 py-3">Phone</th>
                <th className="text-left px-5 py-3">Team</th>
                <th className="text-left px-5 py-3">Can create tasks</th>
                <th className="text-left px-5 py-3">Manage catalog</th>
                <th className="text-right px-5 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {emps.map((e) => (
                <tr key={e.id} data-testid={`employee-row-${e.id}`}>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-[#0055FF] text-white grid place-items-center font-heading font-bold text-xs">
                        {e.name.slice(0, 1)}
                      </div>
                      <div className="font-medium">{e.name}</div>
                    </div>
                  </td>
                  <td className="px-5 py-3 mono text-xs text-slate-600">{e.email}</td>
                  <td className="px-5 py-3 mono text-xs text-slate-600">{e.phone}</td>
                  <td className="px-5 py-3">
                    <span className="mono text-[10px] uppercase px-2 py-1 rounded bg-slate-100 text-slate-700">
                      {e.department}
                    </span>
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={!!e.can_create_tasks}
                        onCheckedChange={(v) =>
                          togglePermission(e, "can_create_tasks", v)
                        }
                        data-testid={`employee-perm-create-${e.id}`}
                      />
                      {e.can_create_tasks && (
                        <span className="mono text-[10px] uppercase text-emerald-700 flex items-center gap-1">
                          <ShieldCheck className="w-3 h-3" /> manager
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={!!e.can_manage_marketplace}
                        onCheckedChange={(v) =>
                          togglePermission(e, "can_manage_marketplace", v)
                        }
                        data-testid={`employee-perm-marketplace-${e.id}`}
                      />
                      {e.can_manage_marketplace && (
                        <span className="mono text-[10px] uppercase text-[#0055FF] flex items-center gap-1">
                          <Package className="w-3 h-3" /> catalog
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-5 py-3 text-right whitespace-nowrap">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => startEdit(e)}
                      className="text-slate-400 hover:text-[#0055FF]"
                      aria-label={`Edit ${e.name}`}
                      data-testid={`employee-edit-${e.id}`}
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => remove(e.id)}
                      className="text-slate-400 hover:text-red-500"
                      data-testid={`employee-delete-${e.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </td>
                </tr>
              ))}
              {emps.length === 0 && (
                <tr>
                  <td
                    colSpan={7}
                    className="text-center px-5 py-10 text-slate-500 text-sm"
                  >
                    No employees yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit employee</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div className="space-y-2">
                <Label>Name</Label>
                <Input
                  value={editing.name}
                  onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                  data-testid="employee-edit-name"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={editing.email}
                    onChange={(e) => setEditing({ ...editing, email: e.target.value })}
                    data-testid="employee-edit-email"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Phone</Label>
                  <Input
                    value={editing.phone}
                    onChange={(e) => setEditing({ ...editing, phone: e.target.value })}
                    data-testid="employee-edit-phone"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Department</Label>
                <Select
                  value={editing.department}
                  onValueChange={(v) => setEditing({ ...editing, department: v })}
                >
                  <SelectTrigger data-testid="employee-edit-department">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {/* Keep a department that's since been renamed/removed selectable. */}
                    {(depts.includes(editing.department) ? depts : [editing.department, ...depts]).map((d) => (
                      <SelectItem key={d} value={d}>
                        {d}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="flex items-center justify-between">
                  <span>New password</span>
                  <button
                    type="button"
                    className="mono text-[10px] uppercase tracking-widest text-[#0055FF] flex items-center gap-1"
                    onClick={() => {
                      setEditing({ ...editing, password: generatePassword() });
                      setShowEditPw(true);
                    }}
                    data-testid="employee-edit-generate-password"
                  >
                    <RefreshCw className="w-3 h-3" /> Generate strong
                  </button>
                </Label>
                <div className="relative">
                  <Input
                    type={showEditPw ? "text" : "password"}
                    value={editing.password}
                    placeholder="Leave blank to keep the current password"
                    onChange={(e) => setEditing({ ...editing, password: e.target.value })}
                    className="pr-9"
                    data-testid="employee-edit-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowEditPw((v) => !v)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                    aria-label="toggle password visibility"
                  >
                    {showEditPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <p className="mono text-[10px] text-slate-500">
                  At least 8 characters. Their old password stops working once you save.
                </p>
              </div>
              {lastReset && (
                <div className="border border-emerald-200 bg-emerald-50 rounded-md p-3 text-sm">
                  <div className="font-medium text-emerald-800">Share the new login with your employee:</div>
                  <div className="mono text-xs text-emerald-900 mt-1">
                    {lastReset.email} / {lastReset.password}
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    className="mt-2"
                    onClick={() => navigator.clipboard.writeText(`${lastReset.email} / ${lastReset.password}`)}
                  >
                    <Copy className="w-3 h-3 mr-1" /> Copy
                  </Button>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>
              {lastReset ? "Done" : "Cancel"}
            </Button>
            <Button
              onClick={saveEdit}
              disabled={savingEdit}
              className="bg-[#0055FF] hover:bg-[#0040CC]"
              data-testid="employee-edit-submit"
            >
              {savingEdit ? "Saving…" : "Save changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
